import React, { useState, useEffect, useMemo } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import Spinner from 'react-bootstrap/Spinner';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import axios from 'axios';
import { useNavigate, Link, useParams, useLocation } from "react-router-dom";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; 

const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL; 
const API_KEY = process.env.REACT_APP_BACKEND_API_KEY; 

const MaterialanforderungPosition = () => {
  const navigate = useNavigate();
  const { id } = useParams(); 
  const { state } = useLocation();

  // Daten aus dem Navigation-State ziehen
  const vorgangsId = state?.vorgangsId ?? '';
  const kdiNr = state?.kdiNr ?? '';
  const monteur = state?.monteur ?? '';
  const strasse = state?.strasse ?? '';
  const vorgangszeitpunkt = state?.vorgangszeitpunkt ?? '';
  
  const bearbeiten = state?.bearbeiten ?? false;
  
  // === NEU: States für die Mitarbeiter-Auswahl (Richter) ===
  const [mitarbeiterListe, setMitarbeiterListe] = useState([]);
  const [richterId, setRichterId] = useState(state?.richter ?? '');
  
  // Die raw-Liste holen und mit useMemo zwischenspeichern
  // Prüft im Bearbeiten Modus, ob Artikel eine InterneBestellung und kein Fall ist
  const artikelListe = useMemo(() => {
    const raw = state?.artikelListe ?? [];
    
    if (!bearbeiten) {
      return raw;
    }
    const bekannteFaelle = raw
      .map(art => art.fall ? String(art.fall).trim().toLowerCase() : '')
      .filter(fallText => fallText !== '');

    return raw.filter(art => {
      const isIntern = art.interneBestellung === true || art.interneBestellung === 1;
      
      const artNrClean = String(art.artNr).trim().toLowerCase();
      const isFallArtikel = bekannteFaelle.includes(artNrClean);

      return isIntern && !isFallArtikel;
    });
  }, [state?.artikelListe, bearbeiten]);

  const [daten, setdaten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [username, setusername] = useState('');

  // === NEU: Name des ausgewählten Richters für die Text-Anzeige ermitteln ===
  const richterNameAnzeige = useMemo(() => {
    if (!richterId || mitarbeiterListe.length === 0) return '-';
    const gefunden = mitarbeiterListe.find(m => String(m.ID) === String(richterId));
    return gefunden ? `${gefunden.Vorname} ${gefunden.Nachname}` : '-';
  }, [richterId, mitarbeiterListe]);

  // === Initialisierung beim Laden ===
  useEffect(() => {
    if (localStorage.getItem("loginData") == null) {
      navigate("/dhworld/login");
      return;
    }

    const d = JSON.parse(localStorage.getItem("loginData"));
    setusername(d.username);

    // Mappen der vom Auswahlschirm übergebenen Artikel auf die UI-States
    const initialisiert = artikelListe.map(art => {
      let initialLagerMenge = '';
      if (art.neuerLagerbestand !== null && 
          art.neuerLagerbestand !== undefined && 
          String(art.neuerLagerbestand) !== 'null') {
        initialLagerMenge = String(art.neuerLagerbestand);
      }

      let initialPickMenge = '';
      if (art.pickMenge !== null && 
          art.pickMenge !== undefined && 
          String(art.pickMenge) !== 'null') {
        initialPickMenge = String(art.pickMenge);
      }

      return {
        ...art,
        pickMengeStr: initialPickMenge,
        lagerMengeStr: initialLagerMenge,
        status: art.pickMenge !== null ? 'geschlossen' : 'offen',
        error: art.pickMenge !== null ? false : true,
      };
    });

    setdaten(initialisiert);

    // === NEU: Mitarbeiter-Daten aus dem Backend abrufen ===
    const fetchMitarbeiter = async () => {
      try {
        const headers = { 
          'Content-Type': 'application/json',
          'x-api-key': API_KEY 
        };
        const response = await axios.get(`${BACKEND_BASE_URL}/matmitarbeiter`, { headers });
        setMitarbeiterListe(response.data);
      } catch (err) {
        console.error("Fehler beim Laden der Mitarbeiter:", err);
        toast.error("Mitarbeiterliste konnte nicht geladen werden!");
      } finally {
        setLoading(false);
      }
    };

    fetchMitarbeiter();
  }, [artikelListe, navigate]);

  // === Logik für Sonderfall-Erkennung ===
  const allowNegativeForPick = (item) => {
    const r = parseFloat(String(item.sollMenge).replace(',', '.'));
    return isNaN(r) || r === -1;
  };

  // === Eingabevalidierung (Zahlen-Parser) ===
  const validateNumber = (value, allowNegative = false) => {
    if (value === '') return '';
    const cleaned = value.replace(',', '.');

    const regex = allowNegative ? /^-?\d*\.?\d*$/ : /^\d*\.?\d*$/;
    if (!regex.test(cleaned)) return '';
    if (cleaned === '-' || cleaned === '.') return '';

    return cleaned;
  };

  // === Wert aktualisieren bei Tastatur-Eingabe ===
  const handleInputChange = (index, field, value) => {
    if (!bearbeiten) return; 

    const updated = [...daten];
    const row = updated[index];

    const allowNeg = field === 'pickMengeStr' && allowNegativeForPick(row);
    const valid = validateNumber(value, allowNeg);
    
    row[field] = valid;
    setdaten(updated);
  };

  // === + / - Klick-Buttons ===
  const adjustValue = (index, field, delta) => {
    if (!bearbeiten) return; 

    const updated = [...daten];
    const row = updated[index];

    let current = parseFloat(row[field] || 0);
    if (isNaN(current)) current = 0;

    const negAllowedForPick = field === 'pickMengeStr' && allowNegativeForPick(row);
    let next = current + delta;

    if (negAllowedForPick) {
      if (next < -1) next = -1;
    } else {
      if (next < 0) next = 0;
    }

    row[field] = String(next);
    setdaten(updated);
  };

  // === Einzelne Zeile abschließen (Schloss) ===
  const toggleStatus = (index) => {
    if (!bearbeiten) return; 

    const updated = [...daten];
    const item = updated[index];

    if (item.status === 'geschlossen') {
      item.status = 'offen';
      item.error = true;
      setdaten(updated);
      return;
    }

    if (item.pickMengeStr === '') {
      toast.warning(`Position ${item.artNr}: Pick-Menge darf nicht leer sein! Bitte Wert (z.B. 0) eintragen.`);
      return;
    }

    const pick = parseFloat(String(item.pickMengeStr).replace(',', '.'));
    const soll = parseFloat(String(item.sollMenge).replace(',', '.'));
    const negAllowed = allowNegativeForPick(item);

    if (negAllowed) {
      if (pick !== -1) {
        toast.warning(`Position ${item.artNr}: Im Sonderfall muss die Pick-Menge genau -1 sein.`);
        return;
      }
    } else {
      const s = isNaN(soll) ? 0 : soll;
      
      if (!(pick === 0 || pick >= s)) {
        toast.warning(`Position ${item.artNr}: Pick-Menge muss 0 oder >= der Soll-Menge (${s}) sein!`);
        return;
      }
    }

    item.status = 'geschlossen';
    item.error = false;
    setdaten(updated);
  };

  // === Daten an das NodeJS-Backend absenden ===
// === Daten an das NodeJS-Backend & PHP-Excel-Skript absenden ===
const handleSubmit = async () => {
  // 1. Pflichtfeld-Validierung für den Richter
  if (!richterId) {
    toast.error("Bitte wählen Sie einen Mitarbeiter (Richter) aus, bevor Sie den Auftrag abschließen!");
    return;
  }

  // 2. Validierung der offenen Positionen
  const offenePositionen = daten.filter(d => d.status !== 'geschlossen');
  if (offenePositionen.length > 0) {
    const updated = daten.map(d => ({
      ...d,
      error: d.status !== 'geschlossen',
    }));
    setdaten(updated);
    toast.error("Es gibt noch offene Positionen, bitte zuerst alle Zeilen mit dem Schloss sperren!");
    return;
  }

  try {
    setLoading(true);
    const headersNode = { 
      'Content-Type': 'application/json',
      'x-api-key': API_KEY 
    };

    // ==========================================
    // SCHRITT A: Updates im NodeJS-Backend speichern
    // ==========================================
    // Schleife über alle Artikel und Updates einzeln abfeuern
    for (const art of daten) {
      const payloadArtikel = {
        pickMenge: parseFloat(art.pickMengeStr) || 0,
        neuerLagerbestand: art.lagerMengeStr === '' ? null : parseFloat(art.lagerMengeStr)
      };

      await axios.put(`${BACKEND_BASE_URL}/artikel/${art.id}`, payloadArtikel, { headers: headersNode });
    }

    // Den Status der Materialanforderung auf "Lager abgeschlossen" setzen
    const payloadAnforderung = {
      lagerAbgeschlossen: true,
      vorgangAbgeschlossen: false,
      richter: parseInt(richterId, 10) 
    };

    await axios.put(`${BACKEND_BASE_URL}/materialanforderungen/${id}`, payloadAnforderung, { headers: headersNode });
    
    // Optionaler Zwischenerfolg im Log
    console.log("NodeJS-Backend erfolgreich aktualisiert.");


    // ==========================================
    // SCHRITT B: Excel-Datei über PHP erzeugen
    // ==========================================
    // Hier mappen wir deine lokalen UI-Zustände auf das von create.php erwartete Format
    const payloadExcel = {
      dokumentNummer: vorgangsId,                     // z.B. "MA-12345"
      vorgangsdatum: vorgangszeitpunkt,
      intnummer: id,                                  // Die interne ID aus der URL/Datenbank
      kommission: kdiNr,                              // KDI-Nummer als Kommission nutzen
      sachbearbeiter: richterNameAnzeige,                       // Der im Lager angemeldete Bearbeiter
      strasse: strasse,                               // Baustellenstraße
      username: monteur,                 // Festwert oder dynamisch, falls benötigt
      positionen: daten.map(d => ({
        artikelnummer: d.artNr,
        bezeichnung: d.kurztext,
        richtMenge: d.sollMenge,
        pickMenge: parseFloat(d.pickMengeStr) || 0,
        lagerMenge: d.lagerMengeStr === '' ? null : parseFloat(d.lagerMengeStr),
        artReferenz: d.lagerort || '',                     
        status: d.status,
      })),
    };
    
    const phpRes = await axios.post(
      'https://dhworld.dietenmeier-harsch.de/create-materialanforderung.php',
      payloadExcel,
      
      { headers: { 'Content-Type': 'application/json' }, withCredentials: true }
    );

    console.log("Response create.php:", phpRes.data);
    
    // Beide Vorgänge waren erfolgreich!
    toast.success(`Materialanforderung ${vorgangsId} abgeschlossen und Excel-Datei erfolgreich erzeugt!`);

    console.log(payloadExcel)
    // Weiterleitung nach kurzem Timeout
    setTimeout(() => {
      navigate(-1); // Oder eine feste Route wie navigate("/dhpick")
    }, 1500);

  } catch (err) {
    console.error("Fehler im Gesamtprozess:", err);
    // Genauer aufschlüsseln, wo es gekracht hat, um das Debugging zu erleichtern
    if (err.config?.url?.includes('create.php')) {
      toast.error("Lagerdaten gespeichert, aber Fehler beim Erzeugen der Excel-Datei!");
    } else {
      toast.error("Verbindungsfehler: Daten konnten nicht im Backend gespeichert werden!");
    }
  } finally {
    setLoading(false);
  }
};

  if (loading) {
    return (
      <div className="text-center" style={{ paddingTop: "150px" }}>
        <Spinner animation="border" variant="success" />
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#eceff2', minHeight: '100vh' }} className="container-fluid">
      <ToastContainer position="top-center" autoClose={2500} />
      
      <div className="row pt-4">
        <div className='col-12'>
          <Link to={-1}>
            <i style={{ fontSize: '25px', color: '#333' }} className="bi bi-arrow-return-left"></i>
          </Link>
          <br /><br />
          <h1>
            Positionen zur Materialanforderung {vorgangsId} 
            {!bearbeiten && <span className="badge bg-secondary ms-3" style={{ fontSize: '1rem' }}>Nur-Lese-Modus</span>} 
          </h1>
          {/* ANGEPASST: Zeigt nun dynamisch den ermittelten Richternamen an */}
          <h5>Monteur: {monteur} | KDI: {kdiNr} | Straße: {strasse} | Richter: {richterNameAnzeige} | Bearbeiter: {username} | Eingangsdatum: {vorgangszeitpunkt ? new Date(vorgangszeitpunkt).toLocaleString('de-DE') : '-'}</h5>
        </div>
      </div>

      <br /><br />

      <Row className="g-4">
        {daten && daten.length > 0 ? (
          <div className="table-responsive-sm">
            <Table bordered hover bg="white">
              <thead className="table-dark">
                <tr>
                  <th>ArtNr</th>
                  <th>Kurztext</th>
                  <th>Lagerort</th>
                  <th>Soll-Menge</th>
                  <th>Bestand (Alt)</th>
                  <th>Typ</th>
                  <th style={{ width: '180px' }}>Pick-Menge</th>
                  <th style={{ width: '220px' }}>Neuer Bestand</th>
                  <th className="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {daten.map((v, index) => {
                  let bgColor = '#fff';
                  if (bearbeiten && v.error) {
                    bgColor = '#ffcccc'; 
                  } else if (v.status === 'geschlossen') {
                    bgColor = '#c8f7c5'; 
                  }
                  
                  const currentPick = parseFloat(v.pickMengeStr) || 0;
                  const expectedStock = v.lagerbestandBeiAbfrage - currentPick;

                  return (
                    <tr key={index} style={{ backgroundColor: bgColor, transition: 'background-color 0.3s' }}>
                      <td>{v.artNr}</td>
                      <td>{v.kurztext}</td>
                      <td>{v.lagerort}</td>
                      <td><strong>{v.sollMenge}</strong></td>
                      <td>{v.lagerbestandBeiAbfrage}</td>
                      
                      <td>
                        {v.interneBestellung ? (
                          <span className="badge bg-warning text-dark">Intern</span>
                        ) : (
                          <span className="badge bg-light text-secondary border">Normal</span>
                        )}
                        {v.fall && (
                          <span className={`badge ms-1 ${v.fall === 'Overnight' ? 'bg-danger' : 'bg-primary'}`}>
                            {v.fall}
                          </span>
                        )}
                      </td>

                      <td>
                        <div className="d-flex align-items-center justify-content-center gap-1 flex-nowrap">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            style={{ minWidth: '32px' }}
                            disabled={!bearbeiten || v.status === 'geschlossen'}
                            onClick={() => adjustValue(index, 'pickMengeStr', -1)}
                          >
                            <i className="bi bi-dash-lg"></i>
                          </Button>
                          <input
                            type="text"
                            className="form-control text-center"
                            style={{ minWidth: '70px', fontSize: '0.9rem' }}
                            value={v.pickMengeStr}
                            disabled={!bearbeiten || v.status === 'geschlossen'}
                            onChange={(e) => handleInputChange(index, 'pickMengeStr', e.target.value)}
                          />
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            style={{ minWidth: '32px' }}
                            disabled={!bearbeiten || v.status === 'geschlossen'}
                            onClick={() => adjustValue(index, 'pickMengeStr', 1)}
                          >
                            <i className="bi bi-plus-lg"></i>
                          </Button>
                        </div>
                      </td>

                      <td>
                        <div className="d-flex align-items-center justify-content-center gap-1 flex-nowrap">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            style={{ minWidth: '32px' }}
                            disabled={!bearbeiten || v.status === 'geschlossen'}
                            onClick={() => adjustValue(index, 'lagerMengeStr', -1)}
                          >
                            <i className="bi bi-dash-lg"></i>
                          </Button>
                          <input
                            type="text"
                            className="form-control text-center"
                            style={{ minWidth: '70px', fontSize: '0.9rem' }}
                            placeholder={String(expectedStock)} 
                            value={v.lagerMengeStr}
                            disabled={!bearbeiten || v.status === 'geschlossen'}
                            onChange={(e) => handleInputChange(index, 'lagerMengeStr', e.target.value)}
                          />
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            style={{ minWidth: '32px' }}
                            disabled={!bearbeiten || v.status === 'geschlossen'}
                            onClick={() => adjustValue(index, 'lagerMengeStr', 1)}
                          >
                            <i className="bi bi-plus-lg"></i>
                          </Button>
                          
                          <Button
                            variant="outline-danger"
                            size="sm"
                            style={{ minWidth: '32px', marginLeft: '2px' }}
                            disabled={!bearbeiten || v.status === 'geschlossen'}
                            onClick={() => handleInputChange(index, 'lagerMengeStr', '')}
                            title="Feld leeren"
                          >
                            <i className="bi bi-eraser-fill"></i>
                          </Button>
                        </div>
                      </td>

                      <td className="text-center">
                        <i
                          className={`bi ${v.status === 'geschlossen' ? 'bi-lock-fill text-success' : 'bi-unlock text-secondary'}`}
                          style={{ 
                            fontSize: '22px', 
                            cursor: bearbeiten ? 'pointer' : 'not-allowed',
                            opacity: bearbeiten ? 1 : 0.6 
                          }}
                          onClick={() => toggleStatus(index)}
                        ></i>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        ) : (
          <Col xs={12}>
            <div className="p-4 text-center bg-white rounded">
              Keine {bearbeiten ? 'internen' : ''} Artikel-Positionen gefunden.
            </div>
          </Col>
        )}
      </Row>

      {/* === NEU: Dropdown-Menü für Richter-Auswahl (wird nur im Bearbeiten-Modus gerendert) === */}
      {bearbeiten && (
        <Row className="justify-content-center mt-5">
          <Col xs={12} md={6} lg={4}>
            <div className="card shadow-sm border-0 p-3" style={{ borderRadius: '10px' }}>
              <label htmlFor="richterSelect" className="form-label fw-bold text-dark">
                <i className="bi bi-person-badge me-2 text-success"></i>
                Mitarbeiter (Richter) auswählen <span className="text-danger">*</span>
              </label>
              <select
                id="richterSelect"
                className="form-select form-select-lg"
                style={{ fontSize: '16px', borderRadius: '8px', cursor: 'pointer' }}
                value={richterId}
                onChange={(e) => setRichterId(e.target.value)}
              >
                <option value="">-- Bitte wählen --</option>
                {mitarbeiterListe.map((m) => (
                  <option key={m.ID} value={m.ID}>
                    {m.Nachname}, {m.Vorname}
                  </option>
                ))}
              </select>
            </div>
          </Col>
        </Row>
      )}

      {/* === Absenden-Button === */}
      {bearbeiten && (
        <div className="text-center my-4">
          <Button
            onClick={handleSubmit}
            variant="success"
            style={{
              padding: '12px 50px',
              borderRadius: '10px',
              fontWeight: '600',
              fontSize: '18px',
              boxShadow: '0 4px 12px rgba(40,167,69,0.3)',
            }}
          >
            📦 Kommissionierung abschließen & senden
          </Button>
        </div>
      )}
    </div>
  );
};

export default MaterialanforderungPosition;